import { isSameDay, endOfMonth, startOfMonth } from 'date-fns'
import { FC, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import styles from 'styles/components/template.module.scss'
import { Description, MainHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { DeliveryCalendar } from 'components/Calendar'
import { CautionFixedLabel } from 'components/Label'
import { AlertCard, LargeMenuCard } from 'components/Card'
import { State } from 'index'
import { getClosedDates, getCurrentDate, getCurrentMonth, getSpecialHolidays } from 'reducks/calendar/selectors'
import { fetchClosedDates } from 'reducks/calendar/operations'
import { getHolidays } from 'reducks/store/selectors'
import { getRole, getStoreRef } from 'reducks/auth/selectors'
import { Role } from 'lib/role'
import { fetchOrdersInPeriod, initOrders, unsubscribeOrders } from 'reducks/checkout/operations'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { dateToString } from 'lib/helper'
import { usePreparingShippingCount } from 'hooks/shipping'
import { updateEditState } from 'reducks/confirm/operations'
import { fetchStore } from 'reducks/store/operations'
import { useToggleCalendarType } from 'hooks/calendar'
import { TopTabs } from 'components/Tab'
import {
  fetchPreparingStoreShippings,
  initStoreShippings,
  unsubscribeStoreShippings,
} from 'reducks/storeShipping/operations'
import { initStoreDeliveryFees } from 'reducks/storeDeliveryFee/operations'
import { selectDefaultBreadTypes } from 'reducks/auth/operations'

const DeliveryTopTemplate: FC = () => {
  const dispatch = useDispatch()
  const currentDate = useSelector((state: State) => getCurrentDate(state))
  const currentMonth = useSelector((state: State) => getCurrentMonth(state))
  const closedDates = useSelector((state: State) => getClosedDates(state))
  const holidays = useSelector((state: State) => getHolidays(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const role = useSelector((state: State) => getRole(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const roleManager = new Role(role, storeRef)
  const { calendarType, toggleCalendar } = useToggleCalendarType('monthly')

  const { count: recentShippingCount, loading: recentShippingLoading } = usePreparingShippingCount(3)
  const hasRecentShipping = !recentShippingLoading && recentShippingCount > 0

  // storeをrefetch
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStore(storeRef))
    dispatch(updateEditState(false))
  }, [storeRef])

  // 月が変わったら休業日を取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(
      fetchClosedDates(startOfMonth(currentMonth), endOfMonth(currentMonth), holidays, specialHolidays, storeRef)
    )
  }, [currentMonth, holidays, storeRef, specialHolidays])

  // 月が変わったら臨時休業日・営業日を再取得する
  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(currentMonth)))
  }, [currentMonth, storeRef])

  // 表示月が変わったら注文を取得する
  useEffect(() => {
    if (!currentMonth || !storeRef) return
    dispatch(initOrders())
    dispatch(
      fetchOrdersInPeriod(
        startOfMonth(currentMonth),
        endOfMonth(currentMonth),
        'deliver',
        storeRef,
        ['succeeded', 'requires_capture'],
        ['delivery']
      )
    )
    return () => {
      dispatch(unsubscribeOrders())
    }
  }, [currentMonth, storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(initStoreShippings())
    dispatch(fetchPreparingStoreShippings(storeRef))
    return () => {
      dispatch(unsubscribeStoreShippings())
    }
  }, [storeRef])

  // 配送設定から編集→確認後にトップページへ戻ってきたケースに備えて初期化
  useEffect(() => {
    dispatch(initStoreDeliveryFees())
  }, [])

  return (
    <>
      {calendarType === 'daily' &&
        closedDates.some((closedDate) => isSameDay(closedDate, currentDate)) &&
        (storeCalendars.some((storeCalendar) => {
          return storeCalendar.status === 'close' && storeCalendar.date === dateToString(currentDate)
        }) ? (
          <CautionFixedLabel label={'臨時休業日です'} />
        ) : (
          <CautionFixedLabel label={'休業日です'} />
        ))}
      <TopTabs />
      <div className={styles.standard}>
        {hasRecentShipping && (
          <>
            <Spacer size={16} />
            <AlertCard
              description={'注文内容を確認しご対応ください。'}
              headline={'お届け予定日が近い商品がまだ発送されていません。'}
              title={'注意'}
            />
          </>
        )}
        <Spacer size={28} />
        <MainHeadline headline={'取り寄せ（通販）'} />
        <Spacer size={20} />
        <Description description={'任意の日程からお客さまの注文内容を確認できます。'} />
        <Spacer size={20} />
        <DeliveryCalendar calendarType={calendarType} toggleCalendar={toggleCalendar} />
        <Spacer size={48} />
        {roleManager.hasEnoughRole(9) && (
          <>
            <LargeMenuCard
              description={'アプリで取り扱う商品の登録編集や、アプリ上での表示並び替えができます。'}
              iconPath={'/icon/blue/bread.svg'}
              onClick={() => {
                dispatch(selectDefaultBreadTypes(['delivery']))
                dispatch(push('/product'))
              }}
              url={'/product?type=delivery'}
              title={'出品する商品管理'}
            />
            <Spacer size={20} />
            <LargeMenuCard
              description={'取り寄せ商品をお届けできる日の設定を行います'}
              iconPath={'/icon/blue/reservation.svg'}
              subMenus={[
                {
                  description: '取り寄せをお届けできる日をまとめて設定することができます。',
                  iconPath: '/icon/blue/collective_schedule.svg',
                  url: '/schedule/delivery/collective_setting',
                  title: 'お届け可能日一括設定',
                },
              ]}
              url={'/schedule/delivery'}
              title={'お届け可能日設定'}
            />
            <Spacer size={20} />
            <LargeMenuCard
              description={'配送料や、お取り寄せに関する設定を行います。'}
              iconPath={'/icon/blue/delivery.svg'}
              url={'/delivery/setting'}
              title={'配送設定'}
            />
            <Spacer size={20} />
          </>
        )}
        {roleManager.hasEnoughRole(5) && (
          <LargeMenuCard
            description={'お店の売上や簡易レポートを確認することができます。'}
            iconPath={'/icon/blue/revenue.svg'}
            url={'/account/revenue'}
            title={'売上確認'}
          />
        )}
        <Spacer size={72} />
      </div>
    </>
  )
}

export default DeliveryTopTemplate
